import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import theme from './theme';
import Layout from './components/Layout/Layout';
import Dashboard from './pages/Dashboard';
import UndertakersList from './pages/Undertakers/UndertakersList';
import UndertakerForm from './pages/Undertakers/UndertakerForm';
import { LocationsList, LocationForm } from './pages/Locations';
import { ChainForm } from './pages/Chains';
import { ServicesQuestionnaire } from './pages/Public';
import Profile from './pages/Profile/Profile';
import MyClaims from './pages/Claims/MyClaims';
import AdminClaims from './pages/Claims/AdminClaims';
import ApproveUndertakers from './pages/Admin/ApproveUndertakers';
import UserManagement from './pages/Admin/UserManagement';
import ServicesSubmissionManager from './pages/Admin/ServicesSubmissionManager';
import ServicesTemplateEditor from './pages/Admin/ServicesTemplateEditor';
import EmailTemplateEditor from './pages/Admin/EmailTemplateEditor';
import SubmissionReviews from './pages/Admin/SubmissionReviews';
import BulkUpdate from './pages/Admin/BulkUpdate';
import BusinessReviews from './pages/Admin/BusinessReviews';
import ContactMessages from './pages/Admin/ContactMessages';
import { ArticlesList, ArticleForm } from './pages/Articles';
import {
  PerformancePage,
  PerformanceProfile,
  PerformancePrisberegner,
  PerformanceOverview,
  PerformanceStats,
  PerformanceSite,
} from './pages/Performance';
import { Settings } from './pages/Settings';
import Login from './components/Auth/Login';
import ResetPassword from './components/Auth/ResetPassword';
import ProtectedRoute from './components/Auth/ProtectedRoute';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Don't refetch when window regains focus - this prevents losing form data
      refetchOnWindowFocus: false,
      // Don't refetch on reconnect either
      refetchOnReconnect: false,
      // Keep data fresh for 5 minutes
      staleTime: 5 * 60 * 1000,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Public routes (no auth required) */}
            <Route path="/udfyld-services/:token" element={<ServicesQuestionnaire />} />
            <Route path="/" element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                <Route index element={<Dashboard />} />
                <Route path="undertakers" element={<UndertakersList />} />
                <Route path="undertakers/new" element={<UndertakerForm />} />
                <Route path="undertakers/edit/:id" element={<UndertakerForm />} />
                <Route path="chains/new" element={<ChainForm />} />
                <Route path="chains/edit/:id" element={<ChainForm />} />
                <Route path="locations" element={<LocationsList />} />
                <Route path="locations/new" element={<LocationForm />} />
                <Route path="locations/edit/:id" element={<LocationForm />} />
                <Route path="articles" element={<ArticlesList />} />
                <Route path="articles/new" element={<ArticleForm />} />
                <Route path="articles/edit/:id" element={<ArticleForm />} />
                {/* Legacy routes - redirect to locations */}
                <Route path="cities" element={<LocationsList />} />
                <Route path="cities/new" element={<LocationForm />} />
                <Route path="cities/edit/:id" element={<LocationForm />} />
                <Route path="profile" element={<Profile />} />
                <Route path="claims" element={<MyClaims />} />
                <Route path="admin/claims" element={<AdminClaims />} />
                <Route path="admin/approve" element={<ApproveUndertakers />} />
                <Route path="admin/users" element={<UserManagement />} />
                <Route path="admin/services-template" element={<ServicesTemplateEditor />} />
                <Route path="admin/services-submissions" element={<ServicesSubmissionManager />} />
                <Route path="admin/submission-reviews" element={<SubmissionReviews />} />
                <Route path="admin/email-templates" element={<EmailTemplateEditor />} />
                <Route path="admin/bulk-update" element={<BulkUpdate />} />
                <Route path="admin/reviews" element={<BusinessReviews />} />
                <Route path="admin/messages" element={<ContactMessages />} />
                {/* Performance Dashboard */}
                <Route path="performance" element={<PerformancePage />}>
                  <Route index element={<Navigate to="profile" replace />} />
                  <Route path="profile" element={<PerformanceProfile />} />
                  <Route path="prisberegner" element={<PerformancePrisberegner />} />
                  <Route path="overview" element={<PerformanceOverview />} />
                  <Route path="stats" element={<PerformanceStats />} />
                  <Route path="site" element={<PerformanceSite />} />
                </Route>
                <Route path="settings" element={<Settings />} />
              </Route>
            </Route>
          </Routes>
        </Router>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
